import { ScribeRemoteKey } from '@/app-shell/chronicle/analytics/scribe-remote-keys';
import {
  isDeviceEventEnabled,
  trackRemoteRecordingStartBlocked,
} from '@/app-shell/chronicle/analytics/track-remote-device-events';
import { track } from '@/lib/analytics/track';
import { TrackingEventName, type TrackingEventV2 } from '@/models/analytics/eventTypes';
import type {
  BindingCodeSource,
  HardwareBulkSyncPhase,
  HardwareConnectionTier,
  HardwareDecryptionMethod,
  HardwareErrorCode,
  HardwareHelpSurface,
  HardwareRecordingInterruptionReason,
  HardwareRecordingOrigin,
  HardwareRecordingRecoveredFormat,
  HardwareRecordingRecoveredMatchVia,
  HardwareRecordingStopReason,
  HardwareRecoveryAction,
  HardwareSessionSource,
  HardwareSetupStep,
  HardwareTransferMethod,
} from '@/models/analytics/features/chronicle';
import {
  type HardwareDatadogAttributes,
  logHardwareDatadogEvent,
} from '@/utils/analytics/log-hardware-datadog-event';
import { stripDeviceFileNameForAnalytics } from '@/utils/analytics/strip-device-file-name';

/**
 * Dispatch a hardware/Heidi-Remote tracking event with `posthog: true` opted
 * in. All hardware_* / native_firmware_* analytics events go through here so
 * we don't have to remember to pass `options: { posthog: true }` per call.
 *
 * Why opt-in: `trackEventV2` defaults Amplitude + Datadog ON, but PostHog
 * OFF — it's per-event opt-in (see `analyticsScribeModel.ts:1442`). PostHog
 * dashboards (e.g. eu.posthog.com/project/12548/dashboard/617568) consuming
 * Heidi Remote events need this opt-in or they will see zero data.
 */
function dispatchHardwareEvent(
  payload: TrackingEventV2 & { pageUrl?: string | null }
): void {
  track({
    ...payload,
    options: { posthog: true },
  });
  // Desktop analytics reach Datadog as RUM actions only, but the session
  // pipeline dashboards join on the log index — mirror every hardware event
  // there, matching iOS's multi-tracker Datadog sink.
  logHardwareDatadogEvent(
    payload.event_name,
    (payload.props ?? {}) as HardwareDatadogAttributes
  );
}

export function trackHardwareSetupStarted(trigger: 'banner' | 'settings'): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareSetupStarted,
    props: { trigger },
  });
}

export function trackHardwareBluetoothPermissionResult(status: string): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBluetoothPermissionResult,
    props: { status },
  });
}

export function trackHardwareBluetoothSettingsOpened(): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBluetoothSettingsOpened,
    props: {},
  });
}

export function trackHardwareDeviceConnectionFailed(
  errorType: string,
  errorDescription: string,
  bindingCodeSource?: BindingCodeSource,
  errorCode?: HardwareErrorCode
): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceConnectionFailed,
    props: {
      error_type: errorType,
      error_description: errorDescription,
      binding_code_source: bindingCodeSource,
      error_code: errorCode,
    },
  });
}

export function trackHardwareDeviceNotFound(): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceNotFound,
    props: {},
  });
}

export function trackHardwareDeviceAlreadyPaired(): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceAlreadyPaired,
    props: {},
  });
}

export function trackHardwareDeviceLinked(
  deviceId: string,
  bindingCodeSource?: BindingCodeSource
): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceLinked,
    props: { device_id: deviceId, binding_code_source: bindingCodeSource },
  });
}

export function trackHardwareDeviceLinkFailed(
  errorType: string,
  errorDescription: string,
  errorCode?: HardwareErrorCode
): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceLinkFailed,
    props: {
      error_type: errorType,
      error_description: errorDescription,
      error_code: errorCode,
    },
  });
}

export function trackHardwareSetupCompleted(hadDefaultTemplate: boolean): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareSetupCompleted,
    props: { had_default_template: hadDefaultTemplate },
  });
}

export function trackHardwareSetupStepViewed({
  step,
  stepIndex,
}: {
  step: HardwareSetupStep;
  stepIndex: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareSetupStepViewed,
    props: { step, step_index: stepIndex },
  });
}

export function trackHardwareInputModeSelected({
  trigger,
  sessionIdLocal,
}: {
  trigger: 'auto' | 'manual';
  sessionIdLocal: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareInputModeSelected,
    props: { mode: 'chronicle', trigger, session_id_local: sessionIdLocal },
  });
}

export function trackHardwareRecordingStart({
  sessionIdLocal,
  sessionId,
  origin,
}: {
  sessionIdLocal: string;
  sessionId?: string | null;
  origin: HardwareRecordingOrigin;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingStart,
    props: {
      session_id_local: sessionIdLocal,
      session_id: sessionId ?? undefined,
      origin,
    },
  });
}

export function trackHardwareRecordingStartFailed({
  sessionIdLocal,
  origin,
  errorType,
  errorDescription,
  errorCode,
}: {
  sessionIdLocal?: string | null;
  origin: HardwareRecordingOrigin;
  errorType: string;
  errorDescription: string;
  errorCode?: HardwareErrorCode;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingStartFailed,
    props: {
      session_id_local: sessionIdLocal ?? undefined,
      origin,
      error_type: errorType,
      error_description: errorDescription,
      error_code: errorCode,
    },
  });
}

/**
 * Start-blocked catalogue emit + Datadog `scribe.remote.start_refused` mirror.
 *
 * While `DEVICE_EVENT_ENABLED` is false, keep the live
 * `hardware_recording_start_blocked` catalogue event (Amplitude / PostHog /
 * Snowplow). When the gate flips true, fold into catalogue `device_event`
 * (`recording_started` + `outcome: blocked`) instead — do not dual-emit both
 * Amplitude names. Datadog `start_refused` always mirrors either path.
 */
export function trackHardwareRecordingStartBlocked({
  sessionIdLocal,
  reason,
  trigger = 'device_start',
}: {
  sessionIdLocal: string;
  reason: string;
  trigger?: 'app_start' | 'app_resume' | 'device_start' | 'recovered_bind';
}): void {
  if (isDeviceEventEnabled()) {
    trackRemoteRecordingStartBlocked({
      sessionIdLocal,
      trigger,
      reason,
    });
  } else {
    dispatchHardwareEvent({
      event_name: TrackingEventName.HardwareRecordingStartBlocked,
      props: { session_id_local: sessionIdLocal, reason },
    });
  }
  logHardwareDatadogEvent(
    ScribeRemoteKey.StartRefused,
    {
      trigger,
      reason,
      session_id: sessionIdLocal,
      session_id_local: sessionIdLocal,
    },
    'warn'
  );
}

/**
 * Fire once per recording session — when the first audio chunk has been
 * uploaded to the server after recording_start succeeded. `latency_ms` is the
 * wall-clock cost of pipeline warmup (auth + connection + first chunk ready).
 */
export function trackHardwareRecordingFirstChunkUploaded({
  sessionIdLocal,
  latencyMs,
}: {
  sessionIdLocal: string;
  latencyMs: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingFirstChunkUploaded,
    props: {
      session_id_local: sessionIdLocal,
      latency_ms: latencyMs,
    },
  });
}

/**
 * Fire once per chunk that fails after exhausting retries. Do NOT fire per
 * retry attempt — that would flood Amplitude. `attempt` is the count of
 * attempts before giving up (final failure); `retryCount` is cumulative
 * chunk-upload failures so far in this session.
 */
export function trackHardwareRecordingChunkUploadFailed({
  sessionIdLocal,
  chunkIndex,
  attempt,
  retryCount,
  errorCode,
  errorMessage,
}: {
  sessionIdLocal: string;
  chunkIndex?: number;
  attempt: number;
  retryCount: number;
  errorCode?: HardwareErrorCode;
  errorMessage?: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingChunkUploadFailed,
    props: {
      session_id_local: sessionIdLocal,
      chunk_index: chunkIndex,
      attempt,
      retry_count: retryCount,
      error_code: errorCode,
      error_message: errorMessage,
    },
  });
}

export function trackHardwareRecordingStop({
  sessionId,
  sessionIdLocal,
  origin,
  durationSeconds,
  stopReason,
}: {
  sessionId: string;
  sessionIdLocal: string;
  origin: HardwareRecordingOrigin;
  durationSeconds: number;
  stopReason: HardwareRecordingStopReason;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingStop,
    props: {
      session_id: sessionId,
      session_id_local: sessionIdLocal,
      origin,
      duration_seconds: durationSeconds,
      stop_reason: stopReason,
    },
  });
}

export function trackHardwareRecordingStopFailed({
  sessionIdLocal,
  origin,
  errorType,
  errorDescription,
  errorCode,
}: {
  sessionIdLocal?: string | null;
  origin: HardwareRecordingOrigin;
  errorType: string;
  errorDescription: string;
  errorCode?: HardwareErrorCode;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingStopFailed,
    props: {
      session_id_local: sessionIdLocal ?? undefined,
      origin,
      error_type: errorType,
      error_description: errorDescription,
      error_code: errorCode,
    },
  });
}

export function trackHardwareRecordingPause({
  sessionId,
  sessionIdLocal,
  origin,
}: {
  sessionId: string;
  sessionIdLocal: string;
  origin: HardwareRecordingOrigin;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingPause,
    props: { session_id: sessionId, session_id_local: sessionIdLocal, origin },
  });
}

export function trackHardwareRecordingPauseFailed({
  sessionIdLocal,
  origin,
  errorType,
  errorDescription,
  errorCode,
}: {
  sessionIdLocal?: string | null;
  origin: HardwareRecordingOrigin;
  errorType: string;
  errorDescription: string;
  errorCode?: HardwareErrorCode;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingPauseFailed,
    props: {
      session_id_local: sessionIdLocal ?? undefined,
      origin,
      error_type: errorType,
      error_description: errorDescription,
      error_code: errorCode,
    },
  });
}

export function trackHardwareRecordingResume({
  sessionId,
  sessionIdLocal,
  origin,
}: {
  sessionId: string;
  sessionIdLocal: string;
  origin: HardwareRecordingOrigin;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingResume,
    props: { session_id: sessionId, session_id_local: sessionIdLocal, origin },
  });
}

export function trackHardwareRecordingResumeFailed({
  sessionIdLocal,
  origin,
  errorType,
  errorDescription,
  errorCode,
}: {
  sessionIdLocal?: string | null;
  origin: HardwareRecordingOrigin;
  errorType: string;
  errorDescription: string;
  errorCode?: HardwareErrorCode;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingResumeFailed,
    props: {
      session_id_local: sessionIdLocal ?? undefined,
      origin,
      error_type: errorType,
      error_description: errorDescription,
      error_code: errorCode,
    },
  });
}

export function trackHardwareBleReconnected(sessionIdLocal?: string | null): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBleReconnected,
    props: { session_id_local: sessionIdLocal ?? undefined },
  });
}

export function trackHardwareDeviceRecordingStopped({
  sessionIdLocal,
  wasBleDisconnected,
}: {
  sessionIdLocal?: string | null;
  wasBleDisconnected: boolean;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceRecordingStopped,
    props: {
      session_id_local: sessionIdLocal ?? undefined,
      was_ble_disconnected: wasBleDisconnected,
    },
  });
}

export function trackHardwareBulkSyncTriggered(
  trigger: 'auto' | 'manual' | 'reconnect'
): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBulkSyncTriggered,
    props: { trigger },
  });
}

// Forward stub — desktop has no "Resume" banner yet (auto-sync only via Rust).
export function trackHardwareBulkSyncResumed(): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBulkSyncResumed,
    props: {},
  });
}

export function trackHardwareBulkSyncCancelled(
  reason: string,
  transferMethod?: HardwareTransferMethod
): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBulkSyncCancelled,
    props: { reason, transfer_method: transferMethod },
  });
}

export function trackHardwareBulkSyncPhaseChanged({
  phase,
  flowId,
  transferMethod,
}: {
  phase: HardwareBulkSyncPhase;
  flowId?: string;
  transferMethod?: HardwareTransferMethod;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBulkSyncPhaseChanged,
    props: { phase, flow_id: flowId, transfer_method: transferMethod },
  });
}

export function trackHardwareBulkSyncFileFailed({
  deviceFilenameHash,
  fileSizeBytes,
  transferMethod,
  stage,
  errorCode,
  errorMessage,
  flowId,
}: {
  deviceFilenameHash: string;
  fileSizeBytes?: number;
  transferMethod: HardwareTransferMethod;
  stage: 'download' | 'decrypt' | 'upload';
  errorCode: HardwareErrorCode;
  errorMessage?: string;
  flowId?: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBulkSyncFileFailed,
    props: {
      device_filename_hash: deviceFilenameHash,
      file_size_bytes: fileSizeBytes,
      transfer_method: transferMethod,
      stage,
      error_code: errorCode,
      error_message: errorMessage,
      flow_id: flowId,
    },
  });
}

export function trackHardwareBulkSyncFileRetry({
  deviceFilenameHash,
  transferMethod,
  attempt,
  errorCode,
  flowId,
}: {
  deviceFilenameHash: string;
  transferMethod: HardwareTransferMethod;
  attempt: number;
  errorCode: HardwareErrorCode;
  flowId?: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBulkSyncFileRetry,
    props: {
      device_filename_hash: deviceFilenameHash,
      transfer_method: transferMethod,
      attempt,
      error_code: errorCode,
      flow_id: flowId,
    },
  });
}

export function trackHardwareBulkSyncTaskFailed({
  taskId,
  sessionId,
  step,
  errorCode,
  error,
  willRetry,
  flowId,
}: {
  taskId: string;
  sessionId?: string;
  step: string;
  errorCode?: HardwareErrorCode;
  error?: string;
  willRetry: boolean;
  flowId?: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBulkSyncTaskFailed,
    props: {
      task_id: taskId,
      // For Chronicle tasks the engine task_id IS the device filename; the
      // alias keeps dashboards joinable on bare-stem `device_file_name`
      // (strip at emit only — task_id stays the raw FW/engine string).
      // The only caller is Chronicle-scoped (`isChronicleEvent`).
      device_file_name: stripDeviceFileNameForAnalytics(taskId),
      session_id: sessionId,
      step,
      error_code: errorCode,
      error,
      will_retry: willRetry,
      flow_id: flowId,
    },
  });
}

/**
 * iOS parity: fires once per genuinely-new session created
 * from a Chronicle device — device-initiated live recording (auto-session) or
 * a bulk-synced file. Duplicates and recovered interrupted recordings must NOT
 * call this (they emit `hardware_recording_recovered` instead).
 *
 * Routed through `track()` (the Rematch-containment entry point — new call
 * sites can't use the `analyticsScribe` dispatcher, and `dispatchHardwareEvent`
 * would overwrite `options`): this event postdates the Snowplow-first routing
 * policy, so it must opt OUT of Amplitude while keeping the hardware-family
 * PostHog opt-in.
 */
export function trackHardwareSessionCreated({
  sessionSource,
  deviceFileName,
  sessionId,
  fileDurationSeconds,
  fileSizeBytes,
}: {
  sessionSource: HardwareSessionSource;
  deviceFileName?: string;
  sessionId?: string;
  fileDurationSeconds?: number;
  fileSizeBytes?: number;
}): void {
  // Strip known audio suffixes at the analytics emit boundary only —
  // task_id / registry / BLE state keep the FW name as-is.
  const props = {
    session_source: sessionSource,
    device_file_name: stripDeviceFileNameForAnalytics(deviceFileName),
    session_id: sessionId,
    file_duration_seconds: fileDurationSeconds,
    file_size_bytes: fileSizeBytes,
  };
  track({
    event_name: TrackingEventName.HardwareSessionCreated,
    props,
    options: { posthog: true, amplitude: false },
  });
  logHardwareDatadogEvent(TrackingEventName.HardwareSessionCreated, props);
}

/**
 * Firmware-CRC verdict before a device delete, forwarded from the Rust
 * `bulk_download:file_crc_check` event. One call per file per run on the
 * legacy whole-file path.
 *
 * `missing` is deliberately tracked rather than dropped — it is the signal
 * for how much of the fleet reports a CRC at all, which is what decides
 * whether the gate is doing anything on a given firmware version.
 */
export function trackHardwareBulkSyncFileCrcChecked({
  deviceFileName,
  result,
  expectedCrc,
  actualCrc,
  reason,
}: {
  deviceFileName: string;
  result: 'pass' | 'fail' | 'missing';
  expectedCrc?: string;
  actualCrc?: string;
  reason?: 'crc_mismatch_before_processing' | 'crc_io_error_before_processing';
}): void {
  const props = {
    device_file_name: stripDeviceFileNameForAnalytics(deviceFileName) ?? deviceFileName,
    result,
    expected_crc: expectedCrc,
    actual_crc: actualCrc,
    reason,
  };
  track({
    event_name: TrackingEventName.HardwareBulkSyncFileCrcChecked,
    props,
    options: { posthog: true, amplitude: false },
  });
  logHardwareDatadogEvent(
    TrackingEventName.HardwareBulkSyncFileCrcChecked,
    props,
    result === 'fail' ? 'warn' : 'info'
  );
}

/**
 * Per-file decryption outcome during bulk sync, forwarded from the
 * Rust `bulk_download:file_decryption` event. One call per file per run.
 * Routed through `track()` for the same reasons as
 * `trackHardwareSessionCreated`.
 */
export function trackHardwareFileDecryption({
  deviceFileName,
  outcome,
  method,
  reason,
}: {
  deviceFileName: string;
  outcome: 'skipped' | 'success' | 'failed';
  method?: HardwareDecryptionMethod;
  reason?: string;
}): void {
  const options = { posthog: true, amplitude: false } as const;
  if (outcome === 'success') {
    const props = {
      device_file_name: stripDeviceFileNameForAnalytics(deviceFileName) ?? deviceFileName,
      // Rust only emits the two desktop methods; default defensively so a
      // payload drift can never fabricate a `fresh_binding_code` claim.
      method: method ?? 'cached_binding_code',
    };
    track({
      event_name: TrackingEventName.HardwareFileDecryptionSuccess,
      props,
      options,
    });
    logHardwareDatadogEvent(TrackingEventName.HardwareFileDecryptionSuccess, props);
  } else if (outcome === 'failed') {
    const props = {
      device_file_name: stripDeviceFileNameForAnalytics(deviceFileName) ?? deviceFileName,
      reason: reason ?? 'unknown',
    };
    track({
      event_name: TrackingEventName.HardwareFileDecryptionFailed,
      props,
      options,
    });
    logHardwareDatadogEvent(
      TrackingEventName.HardwareFileDecryptionFailed,
      props,
      'warn'
    );
  } else {
    const props = {
      device_file_name: stripDeviceFileNameForAnalytics(deviceFileName) ?? deviceFileName,
    };
    track({
      event_name: TrackingEventName.HardwareFileDecryptionSkipped,
      props,
      options,
    });
    logHardwareDatadogEvent(TrackingEventName.HardwareFileDecryptionSkipped, props);
  }
}

// Forward stub — desktop has no Wi-Fi transfer UI yet (USB-C only).
export function trackHardwareWifiTransferRequested(): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareWifiTransferRequested,
    props: {},
  });
}

export function trackNativeFirmwareUpdateTapped({
  currentVersion,
  availableVersion,
}: {
  currentVersion: string;
  availableVersion: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareUpdateTapped,
    props: {
      current_version: currentVersion,
      available_version: availableVersion,
    },
  });
}

export function trackNativeFirmwareUpdateCheckStart({
  currentVersion,
  deviceModel,
}: {
  currentVersion: string;
  deviceModel: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareUpdateCheckStart,
    props: { current_version: currentVersion, device_model: deviceModel },
  });
}

export function trackNativeFirmwareUpdateCheckFailed({
  errorType,
  errorDescription,
  errorCode,
}: {
  errorType: string;
  errorDescription: string;
  errorCode?: HardwareErrorCode;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareUpdateCheckFailed,
    props: {
      error_type: errorType,
      error_description: errorDescription,
      error_code: errorCode,
    },
  });
}

export function trackNativeFirmwareUpdateAvailable({
  currentVersion,
  availableVersion,
  updateType,
  deviceModel,
}: {
  currentVersion: string;
  availableVersion: string;
  updateType: 'forced' | 'recommended';
  deviceModel: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareUpdateAvailable,
    props: {
      current_version: currentVersion,
      available_version: availableVersion,
      update_type: updateType,
      device_model: deviceModel,
    },
  });
}

export function trackNativeFirmwareUpToDate({
  currentVersion,
  deviceModel,
}: {
  currentVersion: string;
  deviceModel: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareUpToDate,
    props: { current_version: currentVersion, device_model: deviceModel },
  });
}

export function trackNativeFirmwareDownloadStart({
  version,
  deviceModel,
}: {
  version: string;
  deviceModel: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareDownloadStart,
    props: { version, device_model: deviceModel },
  });
}

export function trackNativeFirmwareDownloadComplete({
  version,
  deviceModel,
}: {
  version: string;
  deviceModel: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareDownloadComplete,
    props: { version, device_model: deviceModel },
  });
}

export function trackNativeFirmwareDownloadFailed({
  version,
  deviceModel,
  errorType,
  errorDescription,
  errorCode,
}: {
  version: string;
  deviceModel: string;
  errorType: string;
  errorDescription: string;
  errorCode?: HardwareErrorCode;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareDownloadFailed,
    props: {
      version,
      device_model: deviceModel,
      error_type: errorType,
      error_description: errorDescription,
      error_code: errorCode,
    },
  });
}

export function trackHardwareDetailViewed({
  deviceId,
  firmwareVersion,
  batteryLevel,
  isConnected,
}: {
  deviceId: string;
  firmwareVersion?: string;
  /** Omit when the battery hasn't been read yet — a present `0` is a genuine 0%. */
  batteryLevel?: number;
  isConnected: boolean;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDetailViewed,
    props: {
      device_id: deviceId,
      firmware_version: firmwareVersion,
      battery_level: batteryLevel,
      is_connected: isConnected,
    },
  });
}

export function trackHardwareDeviceRemoveTapped(deviceId: string): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceRemoveTapped,
    props: { device_id: deviceId },
  });
}

export function trackHardwareDeviceRemoved(deviceId: string): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceRemoved,
    props: { device_id: deviceId },
  });
}

export function trackHardwareDeviceRemoveFailed({
  deviceId,
  errorType,
  errorDescription,
}: {
  deviceId: string;
  errorType: string;
  errorDescription: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceRemoveFailed,
    props: {
      device_id: deviceId,
      error_type: errorType,
      error_description: errorDescription,
    },
  });
}

export function trackHardwareUsbModeToggled({
  enabled,
  deviceId,
}: {
  enabled: boolean;
  deviceId: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareUsbModeToggled,
    props: { enabled, device_id: deviceId },
  });
}

// iOS parity: no error_type field.
export function trackHardwareUsbModeToggleFailed({
  deviceId,
  errorDescription,
}: {
  deviceId: string;
  errorDescription: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareUsbModeToggleFailed,
    props: { device_id: deviceId, error_description: errorDescription },
  });
}

// APP-4668: Windows-only — fired once per drive when the OS refuses access
// to a candidate Heidi Remote USB volume. Cohorts the failure by drive type
// (Removable / CdRom / Other(N)) and Win32 error code so we can quantify
// how widespread corp Group Policy / EDR / BitLocker blockers are.
export function trackHardwareUsbAccessDenied({
  osError,
  driveType,
}: {
  osError: number | null;
  driveType: string | null;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareUsbAccessDenied,
    props: { os_error: osError, drive_type: driveType },
  });
}

export function trackHardwareRetryConnectionTapped(): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRetryConnectionTapped,
    props: {},
  });
}

export function trackHardwareLowBatteryShown(batteryLevel: number): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareLowBatteryShown,
    props: { battery_level: batteryLevel },
  });
}

// ── Bulk-sync SQLite migration framework (APP-4647) ───────────────────────

/** Fired when a real schema migration ran on app start (`from < to`). */
export function trackHardwareBulkSyncDbMigrationApplied(
  fromVersion: number,
  toVersion: number
): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBulkSyncDbMigrationApplied,
    props: { from_version: fromVersion, to_version: toVersion },
  });
}

/** Fired for either a previous-boot persisted failure (read from the marker
 *  file on next successful boot) or the current-boot run that errored. */
export function trackHardwareBulkSyncDbMigrationFailed(
  fromVersion: number,
  targetVersion: number,
  errorCode: string
): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareBulkSyncDbMigrationFailed,
    props: {
      from_version: fromVersion,
      target_version: targetVersion,
      error_code: errorCode,
    },
  });
}

// ────────────────────────────────────────────────────────────────────────────
// A.3 Live Recovery (APP-4934)
// ────────────────────────────────────────────────────────────────────────────

export function trackHardwareRecordingInterrupted({
  sessionIdLocal,
  reason,
  durationBeforeInterruptMs,
}: {
  sessionIdLocal: string;
  reason: HardwareRecordingInterruptionReason;
  durationBeforeInterruptMs?: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecordingInterrupted,
    props: {
      session_id_local: sessionIdLocal,
      reason,
      duration_before_interrupt_ms: durationBeforeInterruptMs,
    },
  });
}

export function trackHardwareRecoveryCompleted({
  sessionId,
  sessionIdLocal,
  recoveredChunks,
  totalChunks,
  latencyFromInterruptMs,
}: {
  sessionId: string;
  sessionIdLocal?: string;
  recoveredChunks: number;
  totalChunks: number;
  latencyFromInterruptMs?: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecoveryCompleted,
    props: {
      session_id: sessionId,
      session_id_local: sessionIdLocal,
      recovered_chunks: recoveredChunks,
      total_chunks: totalChunks,
      latency_from_interrupt_ms: latencyFromInterruptMs,
    },
  });
}

/**
 * Lane-A bind event: fired once per `chronicle:recording_recovered` when the
 * device resolves a genuine match/format (not `'unknown'`). The Iglu schema
 * (`com.heidi/hardware_recording_recovered` 1-0-0) enum doesn't cover
 * `'unknown'` and sets `additionalProperties: false`, so callers must not
 * invoke this with an unresolved match/format.
 *
 * Bypasses `dispatchHardwareEvent` on purpose: that chokepoint hardcodes
 * `options: { posthog: true }`, overwriting any caller-supplied `options`
 * and silently dropping `amplitude: false` — so the Datadog mirror that
 * chokepoint provides is inlined here instead. The mirror also carries
 * `session_id` (the dashboards' join key), which the Iglu schema's
 * `additionalProperties: false` keeps out of the tracked props.
 */
export function trackHardwareRecordingRecovered({
  sessionIdLocal,
  recoveredMatchVia,
  recoveredFormat,
  recoveredDurationSeconds,
}: {
  sessionIdLocal: string;
  recoveredMatchVia: HardwareRecordingRecoveredMatchVia;
  recoveredFormat: HardwareRecordingRecoveredFormat;
  recoveredDurationSeconds: number;
}): void {
  track({
    event_name: TrackingEventName.HardwareRecordingRecovered,
    props: {
      session_id_local: sessionIdLocal,
      recovered_match_via: recoveredMatchVia,
      recovered_format: recoveredFormat,
      recovered_duration_seconds: recoveredDurationSeconds,
    },
    options: { posthog: true, amplitude: false },
  });
  logHardwareDatadogEvent(ScribeRemoteKey.Recovery, {
    outcome: 'resumed',
    match_via: recoveredMatchVia,
    resumed_from_persisted: recoveredFormat,
    session_id: sessionIdLocal,
    session_id_local: sessionIdLocal,
    recovered_duration_seconds: recoveredDurationSeconds,
  });
}

export function trackHardwareRecoveryFailed({
  sessionId,
  sessionIdLocal,
  errorCode,
  errorMessage,
  attempts,
}: {
  sessionId: string;
  sessionIdLocal?: string;
  errorCode?: HardwareErrorCode;
  errorMessage?: string;
  attempts: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareRecoveryFailed,
    props: {
      session_id: sessionId,
      session_id_local: sessionIdLocal,
      error_code: errorCode,
      error_message: errorMessage,
      attempts,
    },
  });
}

// ────────────────────────────────────────────────────────────────────────────
// A.4 Connection / Reconnect (APP-4934)
// ────────────────────────────────────────────────────────────────────────────

export function trackHardwareDeviceReconnectAttempt({
  attempt,
  tier,
  elapsedSinceDisconnectMs,
  deviceId,
}: {
  attempt: number;
  tier: HardwareConnectionTier;
  elapsedSinceDisconnectMs: number;
  deviceId?: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceReconnectAttempt,
    props: {
      attempt,
      tier,
      elapsed_since_disconnect_ms: elapsedSinceDisconnectMs,
      device_id: deviceId,
    },
  });
}

export function trackHardwareDeviceReconnectTimeout({
  totalAttempts,
  lastTier,
  totalElapsedMs,
  deviceId,
}: {
  totalAttempts: number;
  lastTier: HardwareConnectionTier;
  totalElapsedMs: number;
  deviceId?: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareDeviceReconnectTimeout,
    props: {
      total_attempts: totalAttempts,
      last_tier: lastTier,
      total_elapsed_ms: totalElapsedMs,
      device_id: deviceId,
    },
  });
}

// ────────────────────────────────────────────────────────────────────────────
// A.6 Permission / Transport fallback (APP-4934)
// ────────────────────────────────────────────────────────────────────────────

export function trackHardwareTransportFallback({
  from,
  to,
  reason,
}: {
  from: HardwareTransferMethod;
  to: HardwareTransferMethod;
  reason: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareTransportFallback,
    props: { from, to, reason },
  });
}

// ────────────────────────────────────────────────────────────────────────────
// A.9 Error Handling (APP-4934)
// ────────────────────────────────────────────────────────────────────────────

export function trackHardwareErrorShown({
  errorCode,
  recoveryActionOffered,
  surface,
  flowId,
}: {
  errorCode: HardwareErrorCode;
  recoveryActionOffered?: string;
  surface: 'modal' | 'toast' | 'inline';
  flowId?: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareErrorShown,
    props: {
      error_code: errorCode,
      recovery_action_offered: recoveryActionOffered,
      surface,
      flow_id: flowId,
    },
  });
}

export function trackHardwareErrorRecoveryActionTaken({
  errorCode,
  action,
  successWithin60s,
  flowId,
}: {
  errorCode: HardwareErrorCode;
  action: HardwareRecoveryAction | (string & {});
  successWithin60s?: boolean;
  flowId?: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareErrorRecoveryActionTaken,
    props: {
      error_code: errorCode,
      action,
      success_within_60s: successWithin60s,
      flow_id: flowId,
    },
  });
}

// ────────────────────────────────────────────────────────────────────────────
// A.7 OTA stage events (APP-4934)
// ────────────────────────────────────────────────────────────────────────────

export function trackNativeFirmwareBatteryCheckFailed({
  deviceId,
  deviceModel,
  batteryLevel,
  threshold,
}: {
  deviceId?: string;
  deviceModel?: string;
  batteryLevel: number;
  threshold: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareBatteryCheckFailed,
    props: {
      device_id: deviceId,
      device_model: deviceModel,
      battery_level: batteryLevel,
      threshold,
    },
  });
}

export function trackNativeFirmwareUploadStart({
  deviceId,
  deviceModel,
  targetFirmwareVersion,
}: {
  deviceId?: string;
  deviceModel?: string;
  targetFirmwareVersion?: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareUploadStart,
    props: {
      device_id: deviceId,
      device_model: deviceModel,
      target_firmware_version: targetFirmwareVersion,
    },
  });
}

export function trackNativeFirmwareUploadComplete({
  deviceId,
  deviceModel,
  targetFirmwareVersion,
  durationMs,
  frameCount,
}: {
  deviceId?: string;
  deviceModel?: string;
  targetFirmwareVersion?: string;
  durationMs?: number;
  frameCount?: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareUploadComplete,
    props: {
      device_id: deviceId,
      device_model: deviceModel,
      target_firmware_version: targetFirmwareVersion,
      duration_ms: durationMs,
      frame_count: frameCount,
    },
  });
}

export function trackNativeFirmwareUploadFailed({
  deviceId,
  deviceModel,
  targetFirmwareVersion,
  errorCode,
  errorMessage,
  frameIndex,
  durationMs,
}: {
  deviceId?: string;
  deviceModel?: string;
  targetFirmwareVersion?: string;
  errorCode?: HardwareErrorCode;
  errorMessage?: string;
  frameIndex?: number;
  durationMs?: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareUploadFailed,
    props: {
      device_id: deviceId,
      device_model: deviceModel,
      target_firmware_version: targetFirmwareVersion,
      error_code: errorCode,
      error_message: errorMessage,
      frame_index: frameIndex,
      duration_ms: durationMs,
    },
  });
}

export function trackNativeFirmwareApplyStart({
  deviceId,
  deviceModel,
  targetFirmwareVersion,
}: {
  deviceId?: string;
  deviceModel?: string;
  targetFirmwareVersion?: string;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareApplyStart,
    props: {
      device_id: deviceId,
      device_model: deviceModel,
      target_firmware_version: targetFirmwareVersion,
    },
  });
}

export function trackNativeFirmwareApplyComplete({
  deviceId,
  deviceModel,
  targetFirmwareVersion,
  durationMs,
}: {
  deviceId?: string;
  deviceModel?: string;
  targetFirmwareVersion?: string;
  durationMs?: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareApplyComplete,
    props: {
      device_id: deviceId,
      device_model: deviceModel,
      target_firmware_version: targetFirmwareVersion,
      duration_ms: durationMs,
    },
  });
}

export function trackNativeFirmwareApplyFailed({
  deviceId,
  deviceModel,
  targetFirmwareVersion,
  errorCode,
  errorMessage,
  durationMs,
}: {
  deviceId?: string;
  deviceModel?: string;
  targetFirmwareVersion?: string;
  errorCode?: HardwareErrorCode;
  errorMessage?: string;
  durationMs?: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwareApplyFailed,
    props: {
      device_id: deviceId,
      device_model: deviceModel,
      target_firmware_version: targetFirmwareVersion,
      error_code: errorCode,
      error_message: errorMessage,
      duration_ms: durationMs,
    },
  });
}

export function trackNativeFirmwarePostUpdateReconnected({
  deviceId,
  fromVersion,
  toVersion,
  reconnectDurationMs,
}: {
  deviceId?: string;
  fromVersion?: string;
  toVersion?: string;
  reconnectDurationMs: number;
}): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.NativeFirmwarePostUpdateReconnected,
    props: {
      device_id: deviceId,
      from_version: fromVersion,
      to_version: toVersion,
      reconnect_duration_ms: reconnectDurationMs,
    },
  });
}

// ────────────────────────────────────────────────────────────────────────────
// Help / Support CTAs (settings page + initial pairing card)
// ────────────────────────────────────────────────────────────────────────────

export function trackHardwareLearnMoreTapped(surface: HardwareHelpSurface): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareLearnMoreTapped,
    props: { surface },
  });
}

export function trackHardwareContactSupportTapped(surface: HardwareHelpSurface): void {
  dispatchHardwareEvent({
    event_name: TrackingEventName.HardwareContactSupportTapped,
    props: { surface },
  });
}
